import PropTypes from 'prop-types';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import getLinkWithParams from './getLinkWithParams';

const buildEventData =
  actionFlow =>
  (actionType, data = {}) => ({
    data: {
      actionFlow,
      actionType,
      ...data,
    },
  });

const trackEvent = actionFlow => (actionType, data) => {
  MetricsTracker.track({
    name: USER_EVENTS.ELEMENT_INTERACTION,
    ...buildEventData(actionFlow)(actionType, data),
  });
};

const createLogger = actionFlow => ({
  trackEvent: trackEvent(actionFlow),
  buildEventData: buildEventData(actionFlow),
});

export const LOGGER_NAME = Object.freeze({
  SOLUTIONS: 'mrx_solutions',
  NAV: 'mrx_navigation_menu',
  BI_MARKETPLACE_MODAL: 'marketplace_modal',
});

class ActionFlowLogger {
  constructor(actionFlow) {
    this.logger = createLogger(actionFlow);
  }

  registerActionKey = (key, handlerName, handler) => {
    if (!handlerName) {
      this[key] = (...args) => this.logger.trackEvent(...handler(...args));
    } else {
      if (!this[key]) {
        this[key] = {};
      }
      this[key][handlerName] = (...args) => this.logger.trackEvent(...handler(...args));
    }
    return this;
  };
}

const removeSpaces = str => str.replace(' ', '_');

const getLoggedModuleType = moduleType => {
  if (moduleType === 'CONCEPT_TESTING') {
    return 'CTM';
  }
  return moduleType;
};

const getLogger = loggerName => {
  switch (loggerName) {
    case LOGGER_NAME.SOLUTIONS:
    case LOGGER_NAME.NAV:
      return new ActionFlowLogger(loggerName)
        .registerActionKey('click', 'learnMore', moduleTitle => [`CLICK_${removeSpaces(moduleTitle)}_LEARN_MORE`])
        .registerActionKey('click', 'getStarted', moduleTitle => [`CLICK_${removeSpaces(moduleTitle)}_TRY_IT_NOW`])
        .registerActionKey('click', 'navigateTo', dest => [`CLICK_NAVIGATE_TO_${removeSpaces(dest)}`])
        .registerActionKey('click', 'contactUs', moduleTitle => [`CLICK_${removeSpaces(moduleTitle)}_CONTACT_US`]);
    case LOGGER_NAME.BI_MARKETPLACE_MODAL:
      return new ActionFlowLogger(loggerName)
        .registerActionKey('click', 'getStarted', (moduleType, moduleSubType) => [
          'button_click',
          {
            page: 'marketplace',
            button: 'done',
            moduleType: getLoggedModuleType(moduleType),
            module_subtype: moduleSubType,
          },
        ])
        .registerActionKey('click', 'learnMore', () => [
          'button_click',
          {
            page: 'marketplace',
            button: 'learn_more',
          },
        ])
        .registerActionKey('pageLoad', null, () => ['page_load', { page: 'marketplace' }]);
    default:
      throw new Error(`Unrecognized logger type "${loggerName}"`);
  }
};

export const loggerPropType = PropTypes.objectOf(
  PropTypes.oneOfType([PropTypes.func, PropTypes.objectOf(PropTypes.func)])
);

export const getLinkGenerator = config => {
  const { ut_source: utSource, ut_source2: utSource2, ut_source3: utSource3 } = config;
  return (link, conf = {}) => {
    const { ut_source3: utSource3sub, ut_ctatext: utCtaText, anchor } = conf;
    const linkConfig = {
      queryParams: {
        ut_source: utSource,
        ut_source2: utSource2,
      },
    };
    if (utSource3 || utSource3sub) {
      linkConfig.queryParams.ut_source3 = utSource3sub || utSource3;
    }
    if (utCtaText) linkConfig.queryParams.ut_ctatext = utCtaText;
    if (anchor) linkConfig.anchor = anchor;
    return getLinkWithParams(link, linkConfig);
  };
};

export default getLogger;
